import {
  Card,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import React, { ChangeEvent, useState } from 'react';
import { Supplier } from '../../models/supplier.model';
import Button from '@mui/material/Button';
import { toast } from 'react-toastify';
import { NWClient } from '../../client/NWClient';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../common/constants/routes';
import { useAppSelector } from '../../common/hooks/useAppSelector';
import { selectAllCompanies } from '../../slices/companies/companiesSlice';
import { useTranslation } from 'react-i18next';

export const CreateSupplierProfile: React.FC = () => {
  const { t } = useTranslation();
  const company = useAppSelector(selectAllCompanies)[0];
  const [formData, setFormData] = useState<Partial<Supplier>>({
    company: company.id,
    name: '',
    registration_number: '',
    supplier_email: '',
    website: '',
    physical_address: '',
    postal_address: '',
    industry: '',
    supplier_type: '',
    description: '',
  });
  const [fieldsErrors, setFieldsErrors] = useState(null);
  const [pendingStatus, setPendingStatus] = useState(false);
  const navigate = useNavigate();

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    const errors: Record<string, string[]> = {};
    if (!formData.name) {
      errors.name = ['Supplier name is required'];
    }

    if (Object.keys(errors).length > 0) {
      setFieldsErrors(errors);
      return;
    }
    setPendingStatus(true);

    NWClient.post('supplier_profile', formData, true)
      .then((res: Supplier) => {
        toast.success('Supplier profile created successfully!');
        setFormData({
          company: company.id,
          name: '',
          registration_number: '',
          website: '',
          physical_address: '',
          postal_address: '',
          industry: '',
          supplier_type: '',
          description: '',
        });
        setFieldsErrors(null);
        navigate(ROUTES.SUPPLIER_PROFILES + '/' + res.id);
      })
      .catch((error) => {
        setFieldsErrors(error.response.data);
        toast.error('A profile with the same name already exists.');
      })
      .finally(() => {
        setPendingStatus(false);
      });
  };

  const supplierTypeOptions = [
    'Business Partner',
    'Client',
    'Consultant',
    'Contractor',
    'Manufacturer',
    'Supplier of goods and services',
  ];

  return (
    <div style={{ marginTop: '-61px' }} className='profile-details-wrapper'>
      <h1 style={{ marginBottom: '1rem', textTransform: 'none' }}>{`${t(
        'buttons.createSupplier'
      )}`}</h1>
      <form onSubmit={handleSubmit}>
        <Card sx={{ padding: '16px', marginBottom: '1rem' }}>
          <Grid container className='form-controls' spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={`${t('labels.supplierName')} *`}
                onChange={handleInputChange}
                name='name'
                value={formData.name}
                error={Boolean(fieldsErrors?.name)} // Add this line
                helperText={fieldsErrors?.name?.join('.')}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={`${t('labels.supplierRegistrationNumber')}`}
                onChange={handleInputChange}
                name='registration_number'
                value={formData.registration_number}
              />
              {Boolean(fieldsErrors?.registration_number) && (
                <FormHelperText error={true}>
                  {fieldsErrors?.registration_number.join('.')}
                </FormHelperText>
              )}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={`${t('labels.supplierEmail')}`}
                onChange={handleInputChange}
                name='supplier_email'
                value={formData.supplier_email}
              />
              {Boolean(fieldsErrors?.supplier_email) && (
                <FormHelperText error={true}>
                  {fieldsErrors?.supplier_email.join('.')}
                </FormHelperText>
              )}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('labels.website')}
                onChange={handleInputChange}
                name='website'
                value={formData.website}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={`${t('labels.supplierDescription')}`}
                onChange={handleInputChange}
                name='description'
                value={formData.description}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={`${t('labels.physicalAddress')}`}
                onChange={handleInputChange}
                name='physical_address'
                value={formData.physical_address}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={`${t('labels.postalAddress')}`}
                onChange={handleInputChange}
                name='postal_address'
                value={formData.postal_address}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={`${t('labels.industry')}`}
                onChange={handleInputChange}
                name='industry'
                value={formData.industry}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth variant='outlined'>
                <InputLabel htmlFor='supplierType'>{t('labels.type')}</InputLabel>
                <Select
                  fullWidth
                  value={formData.supplier_type}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, supplier_type: e.target.value }))
                  }
                  displayEmpty
                  slotProps={{
                    input: { id: 'supplierType' },
                  }}
                >
                  {supplierTypeOptions.map((option) => (
                    <MenuItem key={option} value={option} onClick={(e) => e.stopPropagation()}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Card>
        <Grid container justifyContent='flex-end' gap={'8px'} mt={2}>
          <Button
            type='button'
            variant='contained'
            size='medium'
            className='btn-default btn-action-basic btn-transparent-initial btn-cancel'
            onClick={() => {
              navigate(ROUTES.SUPPLIER_PROFILES);
            }}
          >
            {`${t('buttons.goBack')}`}
          </Button>
          <Button
            type='submit'
            variant='contained'
            disabled={pendingStatus}
            className='btn-default btn-action-basic'
          >
            {pendingStatus ? 'Submitting...' : 'Submit'}
          </Button>
        </Grid>
      </form>
    </div>
  );
};
